import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { CreateGroupModalProps } from '../types/createGroup';

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onCreateGroup,
  existingGroups
}) => {
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setGroupName('');
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validateGroupName = (name: string): string => {
    if (!name.trim()) {
      return 'Group name is required';
    }
    if (name.trim().length < 2) {
      return 'Group name must be at least 2 characters';
    }
    if (name.trim().length > 50) {
      return 'Group name must be less than 50 characters';
    }
    if (existingGroups.includes(name.trim().toLowerCase())) {
      return 'A group with this name already exists';
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = groupName.trim();
    const validationError = validateGroupName(trimmedName);
    
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      onCreateGroup(trimmedName);
    } catch {
      setError('Failed to create group. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGroupName(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-group-modal-backdrop" onClick={handleBackdropClick}>
      <div className="create-group-modal-container">
        <div className="create-group-modal-header">
          <h2 className="create-group-modal-title">Create New Group</h2>
          <button
            className="create-group-modal-close"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="create-group-modal-body">
          <form onSubmit={handleSubmit} className="create-group-form">
            <div className="create-group-field">
              <label htmlFor="groupName" className="create-group-label">
                Group Name
              </label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={handleInputChange}
                placeholder="Enter group name..."
                className={`create-group-input ${error ? 'create-group-input-error' : ''}`}
                disabled={isSubmitting}
                autoFocus
              />
              {error && (
                <div className="create-group-error">
                  {error}
                </div>
              )}
            </div>
            
            <div className="create-group-actions">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="create-group-btn-cancel"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting || !groupName.trim()}
                className="create-group-btn-primary"
              >
                {isSubmitting ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;